import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { Route, Switch, Redirect } from 'react-router-dom';

import fetch from 'cross-fetch';

import { Helmet } from 'react-helmet';
import Div100vh from 'react-div-100vh';

import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';

import themeOptions from '../themeOptions';

import { hydrateOrganization } from '../state/app';

import Loading from './Loading';
import Error from './Error';

const Location = lazy(() => import('./Location'));
const Finder = lazy(() => import('./Finder'));

// The GTM container every finder has loaded since 2019. It used to sit in
// public/index.html, where it ran before anything a client configures, so a
// consent banner could never hold it back. It now loads after the client's
// consent script. See one-offs/bevfinder-tracking-cleanup-scope.md.
const LEGACY_GTM = `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-NJHP7F2');</script>`;

// Script injector component to properly evaluate scripts.
// Nodes are added in source order, and a blocking external script (no async
// or defer) is waited for before the next node goes in. That is what lets a
// consent tool listed first block the trackers listed after it.
const ScriptInjector = ({ content }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!content || !container) return undefined;

    let cancelled = false;

    // Clear existing content
    container.innerHTML = '';

    // Create a div with the content
    const div = document.createElement('div');
    div.innerHTML = content;

    Array.from(div.childNodes).reduce((previous, node) => previous.then(() => {
      if (cancelled) return undefined;

      if (node.nodeName !== 'SCRIPT') {
        container.appendChild(node.cloneNode(true));
        return undefined;
      }

      const newScript = document.createElement('script');

      // Copy all attributes
      Array.from(node.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      // Copy content
      newScript.innerHTML = node.innerHTML;

      if (!node.src || node.async || node.defer) {
        container.appendChild(newScript);
        return undefined;
      }

      // Never let one slow third party hold up the rest for long.
      return new Promise((resolve) => {
        const timer = setTimeout(resolve, 5000);
        const done = () => {
          clearTimeout(timer);
          resolve();
        };
        newScript.onload = done;
        newScript.onerror = done;
        container.appendChild(newScript);
      });
    }), Promise.resolve());

    return () => {
      cancelled = true;
    };
  }, [content]);

  return <div ref={containerRef} />;
};

// eslint-disable-next-line react/prefer-stateless-function
class App extends React.Component {
  componentDidMount() {
    const { hydrateOrganization } = this.props;

    // Get the window's host for our API call
    let { host: domain } = window.location;

    // Allow custom domain override via env variable
    if (process.env.REACT_APP_TEST_DOMAIN) {
      domain = process.env.REACT_APP_TEST_DOMAIN;
    }

    if (domain) {
      // Grab the data from the frontend endpoint
      fetch(`/api/organizations?domain=${domain}`)
        .then(
          response => response.json(),
          error => console.warn(error),
        )
        .then((data) => {
          // If there is an error
          if (data.error) {
            throw new Error(data.error);
          }

          // Make sure there is data
          if (data[0] && data[0].id) {
            // Hydrate the organization data
            hydrateOrganization(data[0]);
          }
        });
    }
  }

  render() {
    const { loading, organization, queryParams } = this.props;

    // Make and Provide a Material UI Theme
    const theme = createMuiTheme({
      ...themeOptions,
      palette: {
        primary: {
          main: organization.primary_color ? organization.primary_color : '#000000',
          contrastText: organization.primary_text_color ? organization.primary_text_color : '#fff',
        },
        secondary: {
          main: '#111111',
        },
      },
    });

    return (
      <Div100vh className="app-wrap">
        {loading ? (
          <Loading />
        ) : (
          <MuiThemeProvider theme={theme}>
            <Helmet>
              <title>{organization.name} {organization.product_string_single} Finder</title>
              <meta name="description" content={`Find ${organization.name} ${organization.product_string_plural} near you!`} />
            </Helmet>

            {/* Render header scripts with proper script execution */}
            {/* Consent first, so it can block the trackers that follow */}
            <ScriptInjector
              content={[organization.consent_script, LEGACY_GTM, organization.header_scripts]
                .filter(Boolean)
                .join('\n')}
            />

            <Suspense fallback={<Loading />}>
              <Switch>
                <Route exact path="/" render={() => <Location />} />
                <Route exact path="/:lat,:lng/" render={match => <Finder geo={match.match.params} />} />
                <Route exact path="//" strict render={() => <Redirect to={`/${queryParams}`} />} />
                <Route render={() => <Error />} />
              </Switch>
            </Suspense>
            {/* Render footer scripts with proper script execution */}
            {organization.footer_scripts && (
              <ScriptInjector content={organization.footer_scripts} />
            )}
          </MuiThemeProvider>
        )}
      </Div100vh>
    );
  }
}

const mapStateToProps = ({ router, app }) => ({
  loading: app.isLoading,
  organization: app.organization,
  queryParams: router.location.search,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  hydrateOrganization,
}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(App);
